import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { AVATAR_MEDIUM } from '@zr/components/Phil/avatarSizes';
import { PhilHeader } from '@zr/components/PhilHeader/PhilHeader';

import { setExternalPreference } from '@zr/utilities/api/preference';
import logScreenView from '@zr/utilities/logScreenView';


import './EligibilityCelebration.less';

import {
  STRING_CELEBRATION_CONTINUE,
  STRING_CELEBRATION_GREAT_MATCH_DESCRIPTION,
  STRING_CELEBRATION_GREAT_MATCH_TITLE,
  STRING_CELEBRATION_INVITE_APPLY_DESCRIPTION,
  STRING_CELEBRATION_INVITE_APPLY_TITLE,
  STRING_CELEBRATION_ONE_CLICK_DESCRIPTION,
  STRING_CELEBRATION_ONE_CLICK_TITLE,
  SECOND_TIME_STRING_CELEBRATION_TITLE,
  SECOND_TIME_CELEBRATION_DESCRIPTION_TEXT,
  SECOND_TIME_CONTINUE_BUTTON_TEXT,
  STRING_PHIL_TITLE,
} from './labels';

const SCREEN_TYPE = 'eligibility_celebration';

export function EligibilityCelebration({
  containerElementSelector, logScreenViewArea, onContinue, useDesktopContainerStyles, useResponsiveMobileButton,
  firstTimeMeetingPhil,
}) {
  const handleClick = () => {
    if (logScreenViewArea) {
      logScreenView(logScreenViewArea, SCREEN_TYPE, {
        screen_step_name: 'completed',
      });
    }
    onContinue();
  };

  useEffect(() => {
    if (logScreenViewArea) {
      logScreenView(logScreenViewArea, SCREEN_TYPE);
    }
  }, [logScreenViewArea]);

  // preference that prevents seeing this again
  useEffect(() => {
    setExternalPreference('contact', 'seen_eligibility_celebration', 'yes');
  }, []);

  // behavior below handles the mobile button switching between fixed and not fixed when scrolling to the bottom

  // if container specified, use this element for scrolling calculations
  const queryContainer = useCallback(() => (
    containerElementSelector ? document.querySelector(containerElementSelector) : null
  ), [containerElementSelector]);

  // checks when they've scrolled to the bottom of this content
  const isScrolledToBottom = useCallback(() => {
    const content = document.querySelector('.EligibilityCelebration');
    let scrollBottom;
    // get how far they've scrolled down
    // normally just check window, but in something like a modal check the scroll value in the element instead
    const container = queryContainer();
    if (container) {
      scrollBottom = container.scrollTop + window.innerHeight;
    } else {
      scrollBottom = window.scrollY + window.innerHeight;
    }
    // bottom position of eligibility celebration content
    const containerBottom = content.offsetTop + content.scrollHeight;
    return scrollBottom >= containerBottom;
  }, [queryContainer]);

  const [fixMobileButton, setFixMobileButton] = useState(false);

  // fix the mobile button until they reach the bottom (presumably to show the legal footer below this celebration)
  const setMobileStyle = useCallback(() => {
    setFixMobileButton(!isScrolledToBottom());
  }, [isScrolledToBottom]);

  useEffect(() => {
    let scroller;
    // only do this fancy responsive animation if prop is set
    if (useResponsiveMobileButton) {
      // set the fixed/not fixed styling initially, and on scroll events
      setMobileStyle();
      // set the scroll listener to the container
      // usually it's window that's scrolling, but in something like a modal add the listener to that element instead
      const container = queryContainer();
      scroller = container || window;
      scroller.addEventListener('scroll', setMobileStyle);
    }
    return () => {
      scroller?.removeEventListener('scroll', setMobileStyle);
    };
  }, [queryContainer, setMobileStyle, useResponsiveMobileButton]);

  return (
    <div className={`EligibilityCelebration ${useDesktopContainerStyles ? 'desktop_container' : ''}`}>
      <div className="content_container">
        <PhilHeader
          headerText={firstTimeMeetingPhil ? STRING_PHIL_TITLE : SECOND_TIME_STRING_CELEBRATION_TITLE}
          subheaderText={firstTimeMeetingPhil ? null : SECOND_TIME_CELEBRATION_DESCRIPTION_TEXT}
          size={AVATAR_MEDIUM}
        />
        <div className="blurbs">
          <section className="blurb">
            <div className="blurb_text">
              <h2 className="blurb_title">{STRING_CELEBRATION_ONE_CLICK_TITLE}</h2>
              <p className="blurb_description">
                {STRING_CELEBRATION_ONE_CLICK_DESCRIPTION}
              </p>
            </div>
          </section>
          <section className="blurb">
            <div className="blurb_text">
              <h2 className="blurb_title">{STRING_CELEBRATION_INVITE_APPLY_TITLE}</h2>
              <p className="blurb_description">
                {STRING_CELEBRATION_INVITE_APPLY_DESCRIPTION}
              </p>
            </div>
          </section>
          <section className="blurb">
            <div className="blurb_text">
              <h2 className="blurb_title">{STRING_CELEBRATION_GREAT_MATCH_TITLE}</h2>
              <p className="blurb_description">
                {STRING_CELEBRATION_GREAT_MATCH_DESCRIPTION}
              </p>
            </div>
          </section>
        </div>
      </div>
      <div className={`button_container ${fixMobileButton ? 'fix_mobile_button' : 'scroll_mobile_button'}`}>
        <button
          className={`continue_button ${fixMobileButton ? 'fix_mobile_button' : 'scroll_mobile_button'}`}
          onClick={handleClick}
          type="button"
        >
          {firstTimeMeetingPhil ? STRING_CELEBRATION_CONTINUE : SECOND_TIME_CONTINUE_BUTTON_TEXT}
        </button>
      </div>
    </div>
  );
}

EligibilityCelebration.propTypes = {
  containerElementSelector: PropTypes.string,
  logScreenViewArea: PropTypes.string,
  onContinue: PropTypes.func.isRequired,
  useDesktopContainerStyles: PropTypes.bool,
  useResponsiveMobileButton: PropTypes.bool,
  firstTimeMeetingPhil: PropTypes.bool,
};

EligibilityCelebration.defaultProps = {
  containerElementSelector: null,
  logScreenViewArea: null,
  useDesktopContainerStyles: false,
  useResponsiveMobileButton: false,
  firstTimeMeetingPhil: true,
};

export default EligibilityCelebration;
